import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Eye, ShoppingCart, Check } from "lucide-react";
import { Watermark } from "@/components/Watermark";
import { useI18n } from "@/context/I18nContext";
import { useCart } from "@/context/CartContext";
import { assetImage, formatIDR } from "@/lib/api";
import { toast } from "sonner";

export function AssetCard({ asset, index = 0 }) {
  const { t, localizedTitle } = useI18n();
  const { addItem, inCart } = useCart();
  const navigate = useNavigate();
  const added = inCart(asset.id);

  const handleAdd = (e) => {
    e.stopPropagation();
    addItem(asset);
    toast.success(`${localizedTitle(asset)} → ${t("card.addToCart")}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.4) }}
      className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-xl transition-all duration-300 cursor-pointer"
      onClick={() => navigate(`/asset/${asset.id}`)}
      data-testid={`asset-card-${asset.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img
          src={assetImage(asset)}
          alt={localizedTitle(asset)}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => (e.currentTarget.style.opacity = 0.3)}
        />
        <Watermark />
        {asset.featured && (
          <span className="absolute top-3 left-3 z-10 rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-900">
            ★ Featured
          </span>
        )}
        <span className="absolute top-3 right-3 z-10 rounded-md bg-black/60 backdrop-blur px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-white">
          {asset.asset_type === "photo" ? t("common.photo") : t("common.graphic")}
        </span>
        <div className="absolute inset-0 z-10 flex items-end justify-between gap-2 bg-gradient-to-t from-black/70 via-transparent to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/asset/${asset.id}`);
            }}
            className="flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-2 text-xs font-semibold text-slate-900 hover:bg-white transition"
            data-testid={`asset-preview-${asset.id}`}
          >
            <Eye size={14} /> {t("card.preview")}
          </button>
          <button
            onClick={handleAdd}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ${
              added ? "bg-emerald-500 text-white" : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            data-testid={`asset-add-cart-${asset.id}`}
          >
            {added ? <Check size={14} /> : <ShoppingCart size={14} />}
          </button>
        </div>
      </div>
      <div className="p-4">
        <h3 className="line-clamp-1 font-semibold text-slate-900 text-sm">{localizedTitle(asset)}</h3>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            {t("card.by")} {asset.contributor_name}
          </span>
          <span className="flex items-center gap-1 text-[11px] text-slate-400">
            <Download size={11} /> {asset.downloads}
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="font-mono font-bold text-slate-900">{formatIDR(asset.price)}</span>
          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
            {asset.category}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
